/**
 * CategorySelector Component (WP-DOC-26)
 * Inline category dropdown for observation category editing in staged import review.
 *
 * Features:
 * - Shows current effective category with source indicator
 * - Dropdown with search/filter for category selection
 * - Groups categories by type (Income/Expense/Transfer)
 * - "Clear override" option to revert to LLM suggestion
 * - Saves changes immediately via PATCH API
 * - Optimistic updates with error rollback
 */
import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useCategories } from '@/hooks/useCategories';
import { useUpdateObservationCategory } from '@/hooks/useDocuments';
import { cn } from '@/utils/cn';
import type { Category, CategoryType } from '@/types';

/**
 * Category source indicator type
 * - user: User has overridden the category (blue)
 * - llm-high: LLM suggestion with high confidence >= 0.75 (green)
 * - llm-low: LLM suggestion with low confidence < 0.75 (yellow)
 * - none: No category assigned (gray)
 */
export type CategorySource = 'user' | 'llm-high' | 'llm-low' | 'none';

export interface CategorySelectorProps {
  /** Observation ID */
  observationId: string;
  /** Staged import ID */
  stagedImportId: string;
  /** Current effective category ID */
  currentCategoryId: string | null;
  /** Current effective category name for display */
  currentCategoryName: string | null;
  /** Suggested category ID from LLM */
  suggestedCategoryId: string | null;
  /** Suggested category name from LLM */
  suggestedCategoryName: string | null;
  /** Category confidence from LLM (0-1) */
  categoryConfidence: number | null;
  /** Whether user has overridden the category */
  isUserOverride: boolean;
  /** Callback when category changes (for optimistic updates) */
  onCategoryChange?: (categoryId: string | null, categoryName: string | null) => void;
  /** Category type filter (income/expense) based on transaction amount */
  categoryType?: CategoryType;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Additional CSS class */
  className?: string;
}

/**
 * Determine the category source for visual indicator
 */
function getCategorySource(
  isUserOverride: boolean,
  categoryConfidence: number | null,
  effectiveCategoryId: string | null
): CategorySource {
  if (!effectiveCategoryId) {
    return 'none';
  }
  if (isUserOverride) {
    return 'user';
  }
  if (categoryConfidence !== null && categoryConfidence >= 0.75) {
    return 'llm-high';
  }
  return 'llm-low';
}

/**
 * Source indicator icon component
 */
function SourceIndicator({ source }: { source: CategorySource }) {
  switch (source) {
    case 'user':
      return (
        <span
          className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-100 text-blue-600"
          title="User override"
          aria-label="Category set by user"
        >
          <UserIcon />
        </span>
      );
    case 'llm-high':
      return (
        <span
          className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-green-100 text-green-600"
          title="LLM suggestion (high confidence)"
          aria-label="Category suggested by AI"
        >
          <CheckIcon />
        </span>
      );
    case 'llm-low':
      return (
        <span
          className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-yellow-100 text-yellow-600"
          title="LLM suggestion (low confidence)"
          aria-label="Category suggested by AI (low confidence)"
        >
          <QuestionIcon />
        </span>
      );
    case 'none':
    default:
      return (
        <span
          className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-100 text-gray-400"
          title="Uncategorized"
          aria-label="No category assigned"
        >
          <DashIcon />
        </span>
      );
  }
}

export function CategorySelector({
  observationId,
  stagedImportId,
  currentCategoryId,
  currentCategoryName,
  suggestedCategoryId,
  suggestedCategoryName,
  categoryConfidence,
  isUserOverride,
  onCategoryChange,
  categoryType,
  disabled = false,
  className,
}: CategorySelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch categories
  const { data: categories = [], isLoading: categoriesLoading } = useCategories({
    type: categoryType,
  });

  // Update category mutation
  const updateMutation = useUpdateObservationCategory();

  // Calculate category source for indicator
  const source = getCategorySource(isUserOverride, categoryConfidence, currentCategoryId);

  // Filter categories by search query
  const filteredCategories = useMemo(() => {
    if (!searchQuery) return categories;
    const query = searchQuery.toLowerCase();
    return categories.filter(
      (cat) =>
        cat.name?.toLowerCase()?.includes(query) ||
        cat.path?.toLowerCase()?.includes(query)
    );
  }, [categories, searchQuery]);

  // Group categories by type
  const groupedCategories = useMemo(() => {
    const groups: Record<CategoryType, Category[]> = {
      income: [],
      expense: [],
      transfer: [],
    };
    filteredCategories.forEach((cat) => {
      if (groups[cat.type]) {
        groups[cat.type].push(cat);
      }
    });
    return groups;
  }, [filteredCategories]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle category selection
  const handleSelect = useCallback(
    async (category: Category | null) => {
      const newCategoryId = category?.id ?? null;
      const newCategoryName = category?.name ?? null;

      // Optimistic update callback
      onCategoryChange?.(newCategoryId, newCategoryName);

      // Close dropdown
      setIsOpen(false);
      setSearchQuery('');

      // Save to server
      try {
        await updateMutation.mutateAsync({
          stagedImportId,
          observationId,
          categoryId: newCategoryId,
        });
      } catch {
        // On error, the query invalidation will restore correct state
        // The error is displayed by the mutation's error state if needed
      }
    },
    [stagedImportId, observationId, onCategoryChange, updateMutation]
  );

  // Handle clear override (revert to suggestion)
  const handleClearOverride = useCallback(() => {
    handleSelect(null);
  }, [handleSelect]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
    }
  };

  // Render a category option
  const renderCategoryOption = (category: Category, isSelected: boolean) => (
    <button
      key={category.id}
      type="button"
      className={cn(
        'w-full px-3 py-2 text-left text-sm hover:bg-gray-100 focus:outline-none focus:bg-gray-100',
        isSelected && 'bg-primary-50 text-primary-700'
      )}
      onClick={() => handleSelect(category)}
      role="option"
      aria-selected={isSelected}
    >
      {category.name}
    </button>
  );

  // Render category group
  const renderCategoryGroup = (type: CategoryType, categories: Category[]) => {
    if (categories.length === 0) return null;

    const typeLabels: Record<CategoryType, string> = {
      income: 'Income',
      expense: 'Expense',
      transfer: 'Transfer',
    };

    return (
      <div key={type}>
        <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
          {typeLabels[type]}
        </div>
        {categories.map((cat) => renderCategoryOption(cat, cat.id === currentCategoryId))}
      </div>
    );
  };

  const displayName = currentCategoryName || 'Uncategorized';
  const showClearOption = isUserOverride && suggestedCategoryId !== null;

  return (
    <div ref={containerRef} className={cn('relative inline-block', className)}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled || updateMutation.isPending}
        className={cn(
          'inline-flex items-center gap-1.5 px-2 py-1 text-sm rounded-md border transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1',
          disabled || updateMutation.isPending
            ? 'bg-gray-50 text-gray-400 cursor-not-allowed border-gray-200'
            : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300 cursor-pointer',
          isOpen && 'ring-2 ring-primary-500 border-primary-500'
        )}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`Category: ${displayName}`}
      >
        <SourceIndicator source={source} />
        <span className="truncate max-w-[120px]">{displayName}</span>
        <ChevronDownIcon className={cn('w-4 h-4 text-gray-400 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="absolute z-20 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
          role="listbox"
          aria-label="Select category"
        >
          {/* Search input */}
          <div className="p-2 border-b border-gray-200">
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Search categories..."
              aria-label="Search categories"
            />
          </div>

          {/* Clear override option */}
          {showClearOption && (
            <div className="border-b border-gray-200">
              <button
                type="button"
                onClick={handleClearOverride}
                className="w-full px-3 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 focus:outline-none focus:bg-blue-50"
              >
                <span className="flex items-center gap-2">
                  <UndoIcon className="w-4 h-4" />
                  <span>Use suggestion: {suggestedCategoryName}</span>
                </span>
              </button>
            </div>
          )}

          {/* Category list */}
          <div className="max-h-60 overflow-y-auto">
            {categoriesLoading ? (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">Loading...</div>
            ) : filteredCategories.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">No categories found</div>
            ) : categoryType ? (
              // If filtered by type, render flat list
              filteredCategories.map((cat) => renderCategoryOption(cat, cat.id === currentCategoryId))
            ) : (
              // Otherwise, render grouped by type
              <>
                {renderCategoryGroup('income', groupedCategories.income)}
                {renderCategoryGroup('expense', groupedCategories.expense)}
                {renderCategoryGroup('transfer', groupedCategories.transfer)}
              </>
            )}
          </div>

          {/* Suggested category hint (if different from current) */}
          {suggestedCategoryName && suggestedCategoryId !== currentCategoryId && !showClearOption && (
            <div className="p-2 border-t border-gray-200 bg-gray-50">
              <p className="text-xs text-gray-500">
                Suggested: <span className="font-medium">{suggestedCategoryName}</span>
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Icons
// ============================================================================

function UserIcon() {
  return (
    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function QuestionIcon() {
  return (
    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function DashIcon() {
  return (
    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
      <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function UndoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
      />
    </svg>
  );
}

export default CategorySelector;
